import { BorderRadius, Color, Spacing, Typography } from '@persona-id/design-tokens';
import { FC, useRef } from 'react';
import styled from 'styled-components';

import {
  NODE_WIDTH,
  WorkflowCanvasCustomEvent,
} from 'dashboard/components/WorkflowEditor/WorkflowCanvas/constants';
import { BaseNodeProps } from 'dashboard/components-lib/FlowPlotter';
import { WorkflowRunStepStatus } from 'dashboard/models/workflow-step';
import { Paragraph } from 'design-system/components/Text';
import Tooltip from 'design-system/components/Tooltip';
import { Elevation } from 'design-system/variables/effect';

const Container = styled.div`
  width: ${NODE_WIDTH}px;
  height: 32px;
  position: relative;
  cursor: pointer;
  user-select: none;
`;

const RoutePill = styled.div.attrs<{ active: boolean; blurred: boolean; completed: boolean }>(
  ({ active, blurred, completed }) => ({
    role: 'button',
    style: {
      borderColor: completed
        ? active
          ? Color.CorePurple900
          : Color.CorePurple600
        : active
          ? Color.CorePurple800
          : Color.CorePurple200,
      filter: blurred ? 'opacity(0.4)' : '',
    },
  }),
)<{ active: boolean; blurred: boolean; completed: boolean }>`
  position: absolute;
  display: flex;
  align-items: center;
  height: 32px;
  left: 50%;
  max-width: ${NODE_WIDTH}px;
  transform: translateX(-50%);
  padding: ${Spacing[2]};
  box-shadow: ${Elevation.Mid};
  background-color: ${Color.PrimaryWhite};
  border: 2px solid;
  border-radius: ${BorderRadius.Lg};

  transition-property: border-color;
  transition-duration: 0.25s;
  transition-timing-function: ease-out;
`;

const RouteNumber = styled.div`
  ${Typography.TextExtraSmall}
  height: 16px;
  min-width: 16px;
  line-height: 16px;
  background-color: ${Color.CoreSlate100};
  border-radius: ${BorderRadius.Md};
  text-align: center;
`;

const RouteName = styled.div`
  ${Typography.TextMediumBold}
  color: ${Color.TextPrimary};
  margin-left: 4px;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

interface NodeRouteNameProps {
  active: boolean;
  parentStepToken: string;
  routeLabel: string;
  routeIndex: number;
  status: WorkflowRunStepStatus | null;
}

const NodeRouteName: FC<NodeRouteNameProps & BaseNodeProps> = ({
  active,
  parentStepToken,
  routeLabel,
  routeIndex,
  status,
  onNodeCustomEvent,
}) => {
  const titleElement = useRef<HTMLDivElement>(null);

  return (
    <Container>
      <RoutePill
        active={active && status !== WorkflowRunStepStatus.Canceled}
        blurred={status === WorkflowRunStepStatus.Canceled}
        completed={status === WorkflowRunStepStatus.Completed}
        onClick={() => {
          onNodeCustomEvent({ key: WorkflowCanvasCustomEvent.Select, parentStepToken });
        }}
      >
        <RouteNumber>{routeIndex}</RouteNumber>
        <Tooltip
          disabled={
            (titleElement?.current?.clientWidth ?? 0) >= (titleElement?.current?.scrollWidth ?? 0)
          }
          side='top'
          trigger={<RouteName ref={titleElement}>{routeLabel}</RouteName>}
        >
          <Paragraph size='md'>{routeLabel}</Paragraph>
        </Tooltip>
      </RoutePill>
    </Container>
  );
};

export default NodeRouteName;
